import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import ScannerInput from '@/components/common/ScannerInput';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, XCircle, FileText, Barcode, Package,
  ArrowRight, PackageCheck, CircleDollarSign, Trash2, ScanBarcode,
  Camera, AlertTriangle,
} from 'lucide-react';

// ── API helpers ────────────────────────────────────────────────────
const loansAPI = {
  list:              (params) => api.get('/supplies/loans', { params }),
  get:               (id) => api.get(`/supplies/loans/${id}`),
  startCheckout:     (request_id) => api.post('/supplies/loans/start-checkout', { request_id }),
  scan:              (id, payload) => api.post(`/supplies/loans/${id}/scan`, payload),
  undoScan:          (id, asset_id) => api.delete(`/supplies/loans/${id}/scan/${asset_id}`),
  deposit:           (id, payload) => api.post(`/supplies/loans/${id}/deposit`, payload),
  finalizeCheckout:  (id, payload) => api.post(`/supplies/loans/${id}/finalize-checkout`, payload),
  startCheckin:      (id) => api.post(`/supplies/loans/${id}/start-checkin`),
  finalizeCheckin:   (id, payload) => api.post(`/supplies/loans/${id}/finalize-checkin`, payload),
  scanMaster:        (code) => api.post('/supplies/loans/scan-master', { code }),
  packingSlipUrl:    (id) => `${process.env.REACT_APP_BACKEND_URL}/api/supplies/loans/${id}/packing-slip`,
  packingSlipBlob:   (id) => api.get(`/supplies/loans/${id}/packing-slip`, { responseType: 'blob' }),
  uploadDamagePhoto: (loanId, assetId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/supplies/loans/${loanId}/scans/${assetId}/damage-photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  damagePhotoBlob:   (loanId, assetId) =>
    api.get(`/supplies/loans/${loanId}/scans/${assetId}/damage-photo`, { responseType: 'blob' }),
};

const requestsAPI = {
  list: (params) => api.get('/supplies/requests', { params }),
};

const STATUS = {
  draft:         { label: 'Checkout Draft',   cls: 'bg-amber-100 text-amber-800' },
  checked_out:   { label: 'Checked Out',      cls: 'bg-emerald-100 text-emerald-800' },
  checkin_draft: { label: 'Check-in Draft',   cls: 'bg-indigo-100 text-indigo-800' },
  returned:      { label: 'Returned',         cls: 'bg-slate-200 text-slate-700' },
  cancelled:     { label: 'Cancelled',        cls: 'bg-slate-200 text-slate-600' },
};

const CONDITIONS = ['new', 'good', 'fair', 'damaged', 'retired'];

function fmt(iso) {
  if (!iso) return '—';
  return iso.split('T')[0];
}

// ═══════════════════════════════════════════════════════════════════
// Loan Session (scan → finalize) — used for both checkout & checkin
// ═══════════════════════════════════════════════════════════════════
function LoanSessionDialog({ open, loanId, mode, onClose }) {
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState('ready');
  const [scanMsg, setScanMsg] = useState(null);
  const [signature, setSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [depositForm, setDepositForm] = useState({ amount: '', reference: '', method: 'QuickBooks manual', notes: '' });
  const [depositOpen, setDepositOpen] = useState(false);
  const [scanCondition, setScanCondition] = useState('good');
  const [scanNote, setScanNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Damage-photo state: hidden file input per-row + a blob:URL cache so
  // uploaded photos preview immediately without re-fetching.
  const damageInputsRef = React.useRef({}); // asset_id → HTMLInputElement
  const [damagePreviews, setDamagePreviews] = useState({}); // asset_id → blob URL

  const refresh = useCallback(async () => {
    if (!loanId) return;
    setLoading(true);
    try {
      const { data } = await loansAPI.get(loanId);
      setLoan(data);
    } catch (e) {
      toast.error('Failed to load loan');
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    if (open) {
      refresh();
      setSignature(''); setNotes(''); setScanCondition('good'); setScanNote('');
      setDamagePreviews((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return {};
      });
    }
    return () => {
      // Best-effort cleanup on close: revoke any blob URLs we still hold.
      setDamagePreviews((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return {};
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refresh]);

  // Refresh the damage-photo preview blob URLs whenever the loan reloads.
  // We fetch the JPEG once per row (KR-auth is applied by the API client)
  // and cache the URL keyed by asset_id.
  useEffect(() => {
    if (!loan || mode !== 'checkin') return;
    (loan.scanned_assets || []).forEach(async (s) => {
      if (!s.damage_photo_key) return;
      if (damagePreviews[s.asset_id]) return;
      try {
        const res = await loansAPI.damagePhotoBlob(loanId, s.asset_id);
        const url = URL.createObjectURL(res.data);
        setDamagePreviews((prev) => ({ ...prev, [s.asset_id]: url }));
      } catch {
        // Silent — the "Add photo" button will remain visible.
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan, mode]);

  const uploadDamagePhoto = async (asset_id, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be ≤ 10 MB'); return; }
    setBusy(true);
    try {
      await loansAPI.uploadDamagePhoto(loanId, asset_id, file);
      // Refresh preview + loan so the badge updates immediately.
      const res = await loansAPI.damagePhotoBlob(loanId, asset_id);
      const url = URL.createObjectURL(res.data);
      setDamagePreviews((prev) => {
        if (prev[asset_id]) URL.revokeObjectURL(prev[asset_id]);
        return { ...prev, [asset_id]: url };
      });
      await refresh();
      toast.success('Damage photo saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Photo upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleScan = async (code) => {
    if (busy) return;
    setBusy(true);
    setScanStatus('scanning'); setScanMsg(null);
    try {
      const { data } = await loansAPI.scan(loanId, {
        ean13: code.trim(),
        condition: scanCondition,
        condition_note: scanNote.trim() || null,
      });
      setLoan(data);
      setScanStatus('success');
      setScanMsg('Scanned');
      setTimeout(() => setScanStatus('ready'), 1500);
    } catch (e) {
      setScanStatus('error');
      setScanMsg(e?.response?.data?.detail || 'Scan failed');
      toast.error(e?.response?.data?.detail || 'Scan failed');
      setTimeout(() => setScanStatus('ready'), 2500);
    } finally {
      setBusy(false);
    }
  };

  const undoScan = async (asset_id) => {
    setBusy(true);
    try {
      const { data } = await loansAPI.undoScan(loanId, asset_id);
      setLoan(data);
    } catch (e) {
      toast.error('Undo failed');
    } finally {
      setBusy(false);
    }
  };

  const saveDeposit = async () => {
    if (!depositForm.amount || !depositForm.reference.trim()) {
      toast.error('Amount and reference are required');
      return;
    }
    try {
      const { data } = await loansAPI.deposit(loanId, {
        amount: Number(depositForm.amount),
        reference: depositForm.reference.trim(),
        method: depositForm.method || null,
        notes: depositForm.notes.trim() || null,
      });
      setLoan(data);
      setDepositOpen(false);
      toast.success('Deposit auth recorded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      if (mode === 'checkout') {
        const { data } = await loansAPI.finalizeCheckout(loanId, {
          signature_name: signature.trim() || null,
          notes: notes.trim() || null,
        });
        setLoan(data);
        toast.success('Checked out! Packing slip generated.');
        // Open the PDF in a new tab via authenticated blob (auth-required URL)
        try {
          const blob = await loansAPI.packingSlipBlob(loanId);
          const url = URL.createObjectURL(blob.data);
          window.open(url, '_blank');
        } catch {}
        onClose?.();
      } else {
        const { data } = await loansAPI.finalizeCheckin(loanId, {
          notes: notes.trim() || null,
        });
        setLoan(data);
        toast.success('Check-in complete — assets available again.');
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally {
      setBusy(false);
    }
  };

  const openPackingSlip = async () => {
    try {
      const blob = await loansAPI.packingSlipBlob(loanId);
      const url = URL.createObjectURL(blob.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Could not open packing slip');
    }
  };

  if (!open) return null;
  const scanned = loan?.scanned_assets || [];
  const expectedTotal = loan?.assigned_asset_ids?.length || 0;
  const scannedCount = mode === 'checkout'
    ? scanned.length
    : scanned.filter((s) => s.returned_at).length;
  const canFinalize = mode === 'checkout'
    ? scannedCount === expectedTotal && expectedTotal > 0
    : scannedCount === scanned.length && scanned.length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === 'checkout' ? 'Check-out session' : 'Check-in session'}
              {loan?.loan_id && <span className="text-slate-500 font-mono text-sm ml-2">{loan.loan_id}</span>}
            </DialogTitle>
            <DialogDescription>
              {mode === 'checkout'
                ? 'Scan every assigned asset. Master barcode + packing slip are generated on finalize.'
                : 'Scan each asset returned by the agent. Adjust condition when they come back damaged.'}
            </DialogDescription>
          </DialogHeader>

          {loading || !loan ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : (
            <div className="space-y-4">
              {loan.request && (
                <div className="rounded-md border p-3 bg-slate-50/60 text-sm">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-slate-900">
                      {loan.request.event_label || 'Event'}
                    </span>
                    <Badge className={STATUS[loan.status]?.cls}>{STATUS[loan.status]?.label}</Badge>
                    <span className="text-slate-500">Event {fmt(loan.request.event_date)} → return {fmt(loan.request.return_date)}</span>
                  </div>
                  {loan.master_barcode && (
                    <div className="mt-1 text-xs font-mono text-slate-700 inline-flex items-center gap-1">
                      <Barcode size={12} /> {loan.master_barcode}
                    </div>
                  )}
                </div>
              )}

              {/* Scanner input */}
              <div className="rounded-md border p-3">
                <div className="flex items-end gap-3 mb-2">
                  <div className="flex-1">
                    <Label className="text-xs">Condition on this scan</Label>
                    <Select value={scanCondition} onValueChange={setScanCondition}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Note (optional)</Label>
                    <Input value={scanNote} onChange={(e) => setScanNote(e.target.value)}
                           placeholder="e.g. scratches on top" className="h-9" />
                  </div>
                </div>
                <ScannerInput
                  onScan={handleScan}
                  status={scanStatus}
                  statusMessage={scanMsg}
                  testId="loan-session-scanner"
                  placeholder="Scan asset EAN-13..."
                />
              </div>

              {/* Progress + list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">
                    {mode === 'checkout' ? 'Scanned' : 'Returned'}: {scannedCount}
                    {mode === 'checkout' ? ` / ${expectedTotal}` : ` / ${scanned.length}`}
                  </div>
                  {loan.deposit_auth ? (
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <CircleDollarSign size={12} /> Deposit auth: ${loan.deposit_auth.amount.toFixed(2)} ({loan.deposit_auth.reference})
                    </span>
                  ) : mode === 'checkout' ? (
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => setDepositOpen(true)}
                            data-testid="add-deposit-btn">
                      <CircleDollarSign size={14} className="mr-1" /> Add deposit (optional)
                    </Button>
                  ) : null}
                </div>
                {scanned.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm border rounded-md">
                    No scans yet.
                  </div>
                ) : (
                  <div className="border rounded-md divide-y">
                    {scanned.map((s, i) => {
                      const needsPhoto = mode === 'checkin' && s.returned_at
                        && ['fair', 'damaged', 'retired'].includes((s.condition_in || '').toLowerCase());
                      const photoUrl = damagePreviews[s.asset_id];
                      return (
                        <div key={s.asset_id} className="p-2.5 flex items-center gap-3 text-sm">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {s.asset?.label || s.ean13}
                            </div>
                            <div className="text-xs font-mono text-slate-500">
                              {s.asset?.type_name && <span className="mr-2">{s.asset.type_name}</span>}
                              {s.ean13 || s.asset?.ean13}
                            </div>
                          </div>
                          {mode === 'checkout' ? (
                            <>
                              <Badge className="bg-slate-100 capitalize">{s.condition_out || '—'}</Badge>
                              {loan.status === 'draft' && (
                                <Button variant="ghost" size="sm" onClick={() => undoScan(s.asset_id)}
                                        className="text-rose-600" title="Undo scan">
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </>
                          ) : (
                            <>
                              <Badge className={s.returned_at ? 'bg-emerald-100 text-emerald-700 capitalize' : 'bg-amber-100 text-amber-700'}>
                                {s.returned_at ? (s.condition_in || 'returned') : 'pending'}
                              </Badge>
                              {needsPhoto && (
                                <>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    capture="environment"
                                    className="hidden"
                                    ref={(el) => { if (el) damageInputsRef.current[s.asset_id] = el; }}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = '';
                                      if (f) uploadDamagePhoto(s.asset_id, f);
                                    }}
                                    data-testid={`damage-photo-input-${s.asset_id}`}
                                  />
                                  {photoUrl ? (
                                    <button
                                      type="button"
                                      onClick={() => damageInputsRef.current[s.asset_id]?.click()}
                                      className="relative w-10 h-10 rounded-md overflow-hidden border border-emerald-400 flex-shrink-0"
                                      title="Replace damage photo"
                                      data-testid={`damage-photo-replace-${s.asset_id}`}
                                    >
                                      <img src={photoUrl} alt="damage" className="w-full h-full object-cover" />
                                    </button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => damageInputsRef.current[s.asset_id]?.click()}
                                      className="text-rose-700 border-rose-200 bg-rose-50 hover:bg-rose-100"
                                      data-testid={`damage-photo-btn-${s.asset_id}`}
                                    >
                                      <Camera size={14} className="mr-1" /> Photo
                                    </Button>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Signature + notes (checkout only) */}
              {mode === 'checkout' && loan.status === 'draft' && (                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Agent signature (typed)</Label>
                    <Input value={signature} onChange={(e) => setSignature(e.target.value)}
                           placeholder="e.g. Juan Perez"
                           data-testid="checkout-signature" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Notes (optional)</Label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                           placeholder="Optional pickup notes" />
                  </div>
                </div>
              )}

              {mode === 'checkin' && loan.status === 'checkin_draft' && (
                <>
                  {(() => {
                    const missing = (loan.scanned_assets || []).filter((s) =>
                      s.returned_at
                      && ['fair', 'damaged', 'retired'].includes((s.condition_in || '').toLowerCase())
                      && !s.damage_photo_key,
                    );
                    if (!missing.length) return null;
                    return (
                      <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 p-2.5 text-xs flex items-start gap-2">
                        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                        <span>
                          {missing.length} item{missing.length === 1 ? '' : 's'} came back damaged without a photo.
                          Capture one so the evidence lives with the loan — you can still finalize without it.
                        </span>
                      </div>
                    );
                  })()}
                  <div className="space-y-1.5">
                    <Label>Check-in notes (optional)</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                              placeholder="Any observations about the returned equipment" rows={2} />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {loan?.master_barcode && (
              <Button type="button" variant="outline" onClick={openPackingSlip}>
                <FileText size={14} className="mr-1" /> Open packing slip
              </Button>
            )}
            <Button variant="outline" onClick={() => onClose?.()}>Close</Button>
            {mode === 'checkout' && loan?.status === 'draft' && (
              <Button onClick={finalize} disabled={!canFinalize || busy} data-testid="finalize-checkout">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <PackageCheck size={14} className="mr-1" />}
                Finalize Check-out
              </Button>
            )}
            {mode === 'checkin' && loan?.status === 'checkin_draft' && (
              <Button onClick={finalize} disabled={!canFinalize || busy} data-testid="finalize-checkin">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 size={14} className="mr-1" />}
                Finalize Check-in
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deposit sub-dialog */}
      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Deposit Authorization</DialogTitle>
            <DialogDescription>
              Manual reference only — the card is not automatically charged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount *</Label>
                <Input type="number" min="0" step="0.01" value={depositForm.amount}
                       onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Input value={depositForm.method}
                       onChange={(e) => setDepositForm({ ...depositForm, method: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reference * (QuickBooks auth # or check #)</Label>
              <Input value={depositForm.reference}
                     onChange={(e) => setDepositForm({ ...depositForm, reference: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={depositForm.notes}
                        onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositOpen(false)}>Cancel</Button>
            <Button onClick={saveDeposit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Checkout tab
// ═══════════════════════════════════════════════════════════════════
export default function CheckoutTab() {
  const [readyRequests, setReadyRequests] = useState([]);
  const [activeLoans, setActiveLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionLoanId, setSessionLoanId] = useState(null);
  const [sessionMode, setSessionMode] = useState('checkout');
  const [masterCode, setMasterCode] = useState('');
  const [masterStatus, setMasterStatus] = useState('ready');
  const [masterMsg, setMasterMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [preparingRes, readyRes, active] = await Promise.all([
        requestsAPI.list({ status: 'preparing' }),
        requestsAPI.list({ status: 'ready' }),  // legacy alias
        loansAPI.list(),
      ]);
      // Union of preparing + legacy ready
      const merged = [
        ...(preparingRes.data.items || []),
        ...(readyRes.data.items || []),
      ];
      setReadyRequests(merged);
      setActiveLoans((active.data.items || []).filter((l) => l.status !== 'returned' && l.status !== 'cancelled'));
    } catch (e) {
      toast.error('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-open a loan when ?loan_id=... comes from the Requests tab
  // ("Scan & Package" button). Fires once, then strips the param so a
  // manual refresh doesn't re-open it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preload = params.get('loan_id');
    if (preload && !sessionLoanId) {
      setSessionMode('checkout');
      setSessionLoanId(preload);
      params.delete('loan_id');
      const next = params.toString();
      window.history.replaceState({}, '',
        `${window.location.pathname}${next ? `?${next}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (request_id) => {
    try {
      const { data } = await loansAPI.startCheckout(request_id);
      setSessionMode('checkout');
      setSessionLoanId(data.loan_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start check-out');
    }
  };

  const openLoan = (loan) => {
    setSessionMode(loan.status === 'checkin_draft' ? 'checkin' : 'checkout');
    setSessionLoanId(loan.loan_id);
  };

  const startCheckin = async (loan) => {
    try {
      await loansAPI.startCheckin(loan.loan_id);
      setSessionMode('checkin');
      setSessionLoanId(loan.loan_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start check-in');
    }
  };

  const handleMasterScan = async (code) => {
    setMasterStatus('scanning'); setMasterMsg(null);
    try {
      const { data } = await loansAPI.scanMaster(code.trim().toUpperCase());
      setMasterStatus('success');
      setMasterMsg(`Loan ${data.loan_id}`);
      // Auto-open the loan; if it's `checked_out`, start check-in
      if (data.status === 'checked_out') {
        try {
          await loansAPI.startCheckin(data.loan_id);
        } catch {}
        setSessionMode('checkin');
      } else if (data.status === 'checkin_draft') {
        setSessionMode('checkin');
      } else {
        setSessionMode('checkout');
      }
      setSessionLoanId(data.loan_id);
      setTimeout(() => setMasterStatus('ready'), 1500);
    } catch (e) {
      setMasterStatus('error');
      setMasterMsg(e?.response?.data?.detail || 'Not found');
      setTimeout(() => setMasterStatus('ready'), 2500);
    }
  };

  return (
    <div className="space-y-6">
      {/* Master-barcode scanner */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScanBarcode size={16} className="text-slate-500" />
            <span className="text-sm font-medium">Scan Master Barcode</span>
            <span className="text-xs text-slate-500">— reopen any packing slip / loan for check-in</span>
          </div>
          <ScannerInput
            onScan={handleMasterScan}
            status={masterStatus}
            statusMessage={masterMsg}
            testId="master-scanner"
            placeholder="Scan the packing slip master barcode (KRL-…)"
          />
        </CardContent>
      </Card>

      {/* Ready-for-pickup requests */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Package size={16} /> Ready for pickup ({readyRequests.length})
        </h3>
        {loading ? (
          <div className="p-6 text-center text-slate-500 border rounded-md">
            <Loader2 className="animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : readyRequests.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm border rounded-md">
            No approved requests waiting for pickup.
          </div>
        ) : (
          <div className="space-y-2">
            {readyRequests.map((r) => (
              <Card key={r.request_id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {r.event_label || r.request_id}
                    </div>
                    <div className="text-xs text-slate-500">
                      Event {fmt(r.event_date)} · {r.items.length} line(s) · {r.assigned_asset_ids?.length || 0} asset(s)
                      {r.target_agent_name && ` · for ${r.target_agent_name}`}
                    </div>
                  </div>
                  <Button onClick={() => startCheckout(r.request_id)}
                          data-testid={`start-checkout-${r.request_id}`}>
                    <ArrowRight size={14} className="mr-1" /> Start Check-out
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Active loans (draft, checked_out, checkin_draft) */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <PackageCheck size={16} /> Active loans ({activeLoans.length})
        </h3>
        {activeLoans.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm border rounded-md">
            No open loans.
          </div>
        ) : (
          <div className="space-y-2">
            {activeLoans.map((l) => (
              <Card key={l.loan_id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">
                        {l.request?.event_label || l.loan_id}
                      </span>
                      <Badge className={STATUS[l.status]?.cls}>{STATUS[l.status]?.label}</Badge>
                      {l.master_barcode && (
                        <span className="text-xs font-mono text-slate-500 inline-flex items-center gap-1">
                          <Barcode size={11} /> {l.master_barcode}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {l.scanned_assets?.length || 0} scanned
                      {l.request && ` · Event ${fmt(l.request.event_date)} → ${fmt(l.request.return_date)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {l.status === 'draft' && (
                      <Button variant="outline" size="sm" onClick={() => openLoan(l)}
                              data-testid={`open-draft-${l.loan_id}`}>
                        Continue check-out
                      </Button>
                    )}
                    {l.status === 'checked_out' && (
                      <Button size="sm" onClick={() => startCheckin(l)}
                              data-testid={`start-checkin-${l.loan_id}`}>
                        Start check-in
                      </Button>
                    )}
                    {l.status === 'checkin_draft' && (
                      <Button size="sm" onClick={() => openLoan(l)}
                              data-testid={`resume-checkin-${l.loan_id}`}>
                        Resume check-in
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <LoanSessionDialog
        open={!!sessionLoanId}
        loanId={sessionLoanId}
        mode={sessionMode}
        onClose={() => { setSessionLoanId(null); load(); }}
      />
    </div>
  );
}
